import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Box, SvgIcon, TextField, Typography } from '@mui/material';
import { useGetProductConfigurationsQuery } from '@/api/v2/productConfigurations';
import { useAuthentication } from '@/hooks/useAuthentication';
import { StarjackTable } from '@/components/Table/v2/Table';
import { usePagination } from '@starjack/shared-components';
import { ProductConfiguration } from '@/models/product';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';
import { ProductConfigurationOverview } from './ProductConfigurationOverview';
import { PencilIcon } from '@heroicons/react/24/solid';
import { useConfigurationPanelContext } from '@/pages/productConfiguration/components/ProductConfigurationBulkUpdate/ConfigurationPanelContext';
import { ProductPageDetails } from '@/pages/productConfiguration/components/ProductPageDetails';
import { ConfigurationsPanelContainer } from '@/pages/productConfiguration/components/ProductConfigurationBulkUpdate/ConfigurationsPanelContainer';

export const ProductConfigurationContent: FunctionComponent = () => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data, isLoading } = useGetProductConfigurationsQuery({
        liftOperatorId,
    });
    const {
        actions: { setPageData, setExplanationVideo },
    } = useSlice(pageDataSlice, 'requestSlice');
    const { isActive, isCollapsed } = useConfigurationPanelContext();
    const [productNameSearch, setProductNameSearch] = useState('');
    const [personTypeSearch, setPersonTypeSearch] = useState('');

    const dataFiltered = useMemo(() => {
        return filterProductConfigurations(
            data,
            productNameSearch,
            personTypeSearch
        );
    }, [data, productNameSearch, personTypeSearch]);

    const { currentItems, setItemsPerPage, setNextPage, itemsPerPage } =
        usePagination({
            items: dataFiltered,
            itemsPerPage: 10,
        });
    const [selectedRows, setSelectedRows] = useState<ProductConfiguration[]>(
        []
    );

    const containerMarginBottom = isActive ? (!isCollapsed ? 88 : 6) : 0;

    const { active, total } = useMemo(() => {
        if (!data) {
            return { active: 0, total: 0 };
        }
        return data.reduce(
            (acc, current) => {
                const nextActive = current.enabled
                    ? acc.active + 1
                    : acc.active;
                return {
                    ...acc,
                    active: nextActive,
                };
            },
            { active: 0, total: data.length }
        );
    }, [data]);

    useEffect(() => {
        setPageData({
            title: 'Produktkonfiguration',
            body: <ProductPageDetails />,
        });
        setExplanationVideo(
            'https://my-starjack.fleeq.io/l/vvs11f19di-t7m2ptksa0'
        );
    }, []);

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Produktkonfiguration
            </Typography>
            <Box
                sx={{
                    mb: containerMarginBottom,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        p: 3,
                    }}
                >
                    <Typography>
                        Aktuell sind <strong>{active}</strong> von{' '}
                        <strong>{total}</strong> Tickets zum Verkauf aktiviert
                    </Typography>
                    <TextField
                        sx={{
                            width: 440,
                            ml: 'auto',
                            mr: 2,
                        }}
                        onChange={(e) => {
                            const { value } = e.target;
                            setProductNameSearch(value);
                        }}
                        label='Nach Ticket -/ Produktname suchen'
                    />
                    <TextField
                        sx={{
                            width: '320px',
                        }}
                        onChange={(e) => {
                            const { value } = e.target;
                            setPersonTypeSearch(value);
                        }}
                        label='Nach Personentyp suchen'
                    />
                </Box>
                <StarjackTable<ProductConfiguration>
                    itemId='productId'
                    count={dataFiltered?.length || 0}
                    allItems={dataFiltered}
                    items={currentItems}
                    columns={[
                        {
                            label: 'Produkt',
                            itemTransform: (productConfiguration) =>
                                productConfiguration.product?.name.de,
                        },
                        {
                            label: 'Konfiguration aktiviert',
                            itemTransform: (productConfiguration) => {
                                return productConfiguration.id ? (
                                    <ProductConfigurationOverview
                                        productConfiguration={
                                            productConfiguration
                                        }
                                    />
                                ) : (
                                    'Nein'
                                );
                            },
                        },

                        {
                            label: 'Aktion',
                            itemTransform: (productConfiguration) => (
                                <Link
                                    href={`/produktkonfiguration/details/?id=${productConfiguration.productId}`}
                                >
                                    <IconButton
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        <SvgIcon>
                                            <PencilIcon />
                                        </SvgIcon>
                                    </IconButton>
                                </Link>
                            ),
                        },
                    ]}
                    onPageChange={setNextPage}
                    onRowsPerPageChange={setItemsPerPage}
                    rowsPerPage={itemsPerPage}
                    loading={isLoading}
                    onSelect={(_, allSelected) => {
                        setSelectedRows(allSelected);
                    }}
                    onDeselect={(_, nextSelected) => {
                        setSelectedRows(nextSelected);
                    }}
                    onSelectAll={(selected) => {
                        setSelectedRows(selected);
                    }}
                    onDeselectAll={() => setSelectedRows([])}
                    rowsSelectable
                />

                <ConfigurationsPanelContainer
                    selectedConfigurations={selectedRows}
                />
            </Box>
        </>
    );
};

// TODO: extract this to a global hook that can be used in all sub projects

/**
 * Filters product configurations based on product name and person type search terms
 * Searches both German (.de) and English (.en) fields when available
 * @param data - Array of product configurations to filter
 * @param productNameSearch - Search term for filtering by product name (searches both de and en fields)
 * @param personTypeSearch - Search term for filtering by person type (searches both de and en fields)
 * @returns Filtered array of product configurations
 */
const filterProductConfigurations = (
    data: ProductConfiguration[] | undefined,
    productNameSearch: string,
    personTypeSearch: string
): ProductConfiguration[] => {
    if (!data) {
        return [];
    }

    const filteredByProductName = data.filter((productConfiguration) => {
        const productName = productConfiguration.product?.name;
        if (!productName) return false;

        const searchTerm = productNameSearch.toLowerCase();
        const matchesGerman = productName.de.toLowerCase().includes(searchTerm);
        const matchesEnglish = productName.en
            ?.toLowerCase()
            .includes(searchTerm);

        return matchesGerman || matchesEnglish;
    });

    if (!personTypeSearch.length) {
        return filteredByProductName;
    }

    return filteredByProductName.filter((configuration) =>
        configuration.persons.some((person) => {
            const searchTerm = personTypeSearch.toLowerCase();
            const matchesGerman = person.name.de
                .toLowerCase()
                .includes(searchTerm);
            const matchesEnglish = person.name.en
                ?.toLowerCase()
                .includes(searchTerm);

            return matchesGerman || matchesEnglish;
        })
    );
};
